/**
 * Responsibility: Implement supervisor engine behavior for the engine layer.
 * Scope: Owns engine-layer concerns only; cross-layer orchestration is excluded.
 */
import type { GameState } from "@anyquest/shared-core"
import type {
  SupervisorCheckFn,
  SupervisorCheckMetric,
  SupervisorIncident,
  SupervisorRunSummary,
  SupervisorRemediationResult,
} from "@anyquest/shared-core"
import { getSupervisorConfig, getPolicyForDomain } from './policy'
import { auditLog } from './audit-log'
import { dispatcher } from '../../app-states'
import { TOPICS } from "@anyquest/shared-core"
import { applySupervisorRemediation } from './remediations/default-remediations'
import {
  clearRollbackSnapshots,
  getRollbackSnapshots,
  restoreRollbackSnapshot,
} from './rollback'

const checkRegistry: { id: string, fn: SupervisorCheckFn }[] = []

export function registerCheck(id: string, fn: SupervisorCheckFn) {
  if (!checkRegistry.find(c => c.id === id)) {
    checkRegistry.push({ id, fn })
  }
}

export function getRegisteredChecks(): string[] {
  return checkRegistry.map(c => c.id)
}

let isRunning = false

function resolveTimeBudgetMs(reason: string, baseBudgetMs: number): number {
  return baseBudgetMs
}

function generateId(): string {
  return Math.random().toString(36).substring(2, 9)
}

function markIgnoredByPolicy(incident: SupervisorIncident, policy: ReturnType<typeof getPolicyForDomain>) {
  if (policy === 'observe' || policy === 'freeze') {
    incident.remediationStatus = 'ignored'
    return true
  }
  return false
}

function remediateIncident(state: GameState, incident: SupervisorIncident): SupervisorRemediationResult {
  return applySupervisorRemediation(state, incident)
}

export async function runSupervisorPass(state: GameState, reason = 'unspecified'): Promise<SupervisorRunSummary> {
  const config = getSupervisorConfig()
  const timeBudgetMs = resolveTimeBudgetMs(reason, config.timeBudgetMs)

  if (!config.enabled) {
    return {
      runId: 'skipped',
      reason,
      startTime: Date.now(),
      durationMs: 0,
      incidentsFound: 0,
      incidentsRepaired: 0,
      aborted: false,
      checkMetrics: [],
    }
  }

  if (isRunning) {
    console.warn('[Supervisor] Run aborted: another pass is already in progress.')
    return {
      runId: 'aborted',
      reason,
      startTime: Date.now(),
      durationMs: 0,
      incidentsFound: 0,
      incidentsRepaired: 0,
      aborted: true,
      error: 'Locked',
      checkMetrics: [],
    }
  }

  isRunning = true
  const runId = generateId()
  const epochStart = Date.now()
  const perfStart = performance.now()
  let incidentsFound = 0
  let incidentsRepaired = 0
  const checkMetrics: SupervisorCheckMetric[] = []

  dispatcher.trigger('supervisor', TOPICS.supervisor.runStarted, { runId, reason })

  let aborted = false
  let error: string | undefined

  try {
    for (const check of checkRegistry) {
      const elapsed = performance.now() - perfStart
      if (elapsed > timeBudgetMs) {
        console.warn(`[Supervisor] Time budget exceeded (${timeBudgetMs}ms). Halting checks.`)
        dispatcher.trigger('supervisor', TOPICS.supervisor.degraded, { reason: 'time_budget_exceeded' })
        aborted = true
        break
      }

      const checkStart = performance.now()
      try {
        const result = await check.fn(state)
        const incidents = result.passed ? [] : result.incidents
        checkMetrics.push({
          checkId: check.id,
          durationMs: Math.round((performance.now() - checkStart) * 1000) / 1000,
          incidents: incidents.length,
        })

        for (const rawInc of incidents) {
          incidentsFound++
          const incident: SupervisorIncident = {
            ...rawInc,
            id: generateId(),
            runId,
            timestamp: Date.now(),
            remediationStatus: 'pending',
            checkId: check.id,
          }

          const policy = getPolicyForDomain(incident.domain)
          const ignored = markIgnoredByPolicy(incident, policy)
          if (!ignored) {
            if (policy === 'auto' && incident.confidence >= 0.8) {
              try {
                const remediation = remediateIncident(state, incident)
                if (remediation.applied) {
                  incident.remediationStatus = 'applied'
                  incident.rollbackSnapshotId = remediation.rollbackSnapshotId
                  incidentsRepaired++
                } else {
                  incident.remediationStatus = 'ignored'
                }
              } catch (err) {
                incident.remediationStatus = 'failed'
                incident.remediationError = String(err)
              }
            } else if (policy === 'confirm') {
              incident.remediationStatus = 'pending'
            } else {
              incident.remediationStatus = 'ignored'
            }
          }

          auditLog.recordIncident(incident)
        }
      } catch (checkErr) {
        const checkError = String(checkErr)
        checkMetrics.push({
          checkId: check.id,
          durationMs: Math.round((performance.now() - checkStart) * 1000) / 1000,
          incidents: 0,
          error: checkError,
        })
        console.error(`[Supervisor] Check ${check.id} threw an error:`, checkErr)
        dispatcher.trigger('supervisor', TOPICS.supervisor.degraded, {
          reason: `check_failed:${check.id}`,
        })
      }
    }
  } catch (err) {
    error = String(err)
    aborted = true
    console.error('[Supervisor] Critical failure during run:', err)
    dispatcher.trigger('supervisor', TOPICS.supervisor.degraded, { reason: 'critical_failure' })
  } finally {
    isRunning = false
  }

  const summary: SupervisorRunSummary = {
    runId,
    reason,
    startTime: epochStart,
    durationMs: performance.now() - perfStart,
    incidentsFound,
    incidentsRepaired,
    aborted,
    ...(error ? { error } : {}),
    checkMetrics,
  }

  auditLog.recordRun(summary)
  dispatcher.trigger('supervisor', TOPICS.supervisor.runEnded, {
    runId,
    reason,
    durationMs: summary.durationMs,
    incidents: incidentsFound,
    repaired: incidentsRepaired,
    aborted,
  })

  return summary
}

export function getSupervisorIncidents() {
  return auditLog.getIncidents()
}

export function getSupervisorRuns() {
  return auditLog.getRuns()
}

export function clearSupervisorAuditLog() {
  auditLog.clear()
  clearRollbackSnapshots()
}

export function getSupervisorRollbackSnapshots() {
  return getRollbackSnapshots()
}

export function rollbackSupervisorState(state: GameState, snapshotId: string): boolean {
  return restoreRollbackSnapshot(state, snapshotId)
}

export function remediateIncidentById(state: GameState, incidentId: string): SupervisorRemediationResult {
  const incident = auditLog.getIncidentById(incidentId)
  if (!incident) {
    return { applied: false, changed: false, message: 'Incident not found.' }
  }

  if (incident.remediationStatus === 'applied') {
    return { applied: false, changed: false, message: 'Incident already remediated.' }
  }

  try {
    const result = remediateIncident(state, incident)
    if (result.applied) {
      auditLog.updateIncidentStatus(incident.id, 'applied')
      incident.rollbackSnapshotId = result.rollbackSnapshotId
    } else {
      auditLog.updateIncidentStatus(incident.id, 'ignored')
    }
    return result
  } catch (err) {
    incident.remediationError = String(err)
    auditLog.updateIncidentStatus(incident.id, 'failed')
    return {
      applied: false,
      changed: false,
      message: `Remediation failed: ${String(err)}`,
    }
  }
}
